import React, { useState, useEffect } from 'react';
import { Edit, XCircle } from 'lucide-react';
import { BrandSelector, ModelSelector } from '../BrandModelSelector';

export const EditRepairModal = ({ editRepair, setEditRepair, editForm, setEditForm, saveEdit, savingEdit, api }) => {
  const [brands, setBrands] = useState([]);
  const [modelsMap, setModelsMap] = useState({});
  const [seriesMap, setSeriesMap] = useState({});

  useEffect(() => {
    if (!api) return;
    api.get('/brands-models').then(res => {
      const bList = [], mMap = {}, sMap = {};
      for (const b of res.data) {
        bList.push(b.brand);
        mMap[b.brand] = b.models || [];
        sMap[b.brand] = b.series || {};
      }
      setBrands(bList);
      setModelsMap(mMap);
      setSeriesMap(sMap);
    }).catch(() => {});
  }, [api]);

  if (!editRepair) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setEditRepair(null)}>
      <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="edit-repair-modal">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Edit className="w-5 h-5 text-emerald-400" />
            <h3 className="text-lg font-bold text-white">Modifier {editRepair.ref}</h3>
          </div>
          <button onClick={() => setEditRepair(null)} className="text-gray-400 hover:text-white">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Entreprise</label>
              <div className="input-dark w-full py-2 px-3 text-gray-500 opacity-60">{editRepair.company_name || '-'}</div>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Ref</label>
              <div className="input-dark w-full py-2 px-3 text-gray-500 opacity-60">{editRepair.ref}</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Marque *</label>
              <BrandSelector value={editForm.brand}
                onChange={v => setEditForm(f => ({...f, brand: v, model: ''}))}
                brands={brands}
                accentColor="emerald"
                testId="edit-brand" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Modele *</label>
              <ModelSelector value={editForm.model}
                onChange={v => setEditForm(f => ({...f, model: v}))}
                models={modelsMap[editForm.brand] || []}
                series={seriesMap[editForm.brand] || {}}
                brand={editForm.brand}
                accentColor="emerald"
                testId="edit-model" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Probleme signale</label>
            <input value={editForm.problem} onChange={e => setEditForm(f => ({...f, problem: e.target.value}))} className="input-dark w-full py-2" data-testid="edit-problem" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">IMEI / N° Serie</label>
              <input value={editForm.imei_serial} onChange={e => setEditForm(f => ({...f, imei_serial: e.target.value}))} className="input-dark w-full py-2" data-testid="edit-imei" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Code verrou</label>
              <input value={editForm.device_lock} onChange={e => setEditForm(f => ({...f, device_lock: e.target.value}))} className="input-dark w-full py-2" data-testid="edit-device-lock" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-2">Pieces non fonctionnelles</label>
            <div className="flex flex-wrap gap-2">
              {[
                {key: 'speaker', label: 'Haut-parleur'}, {key: 'lcd', label: 'Ecran'},
                {key: 'front_cam', label: 'Camera avant'}, {key: 'back_cam', label: 'Camera arriere'},
                {key: 'microphone', label: 'Micro'}, {key: 'fingerprint', label: 'Empreinte'},
                {key: 'home_button', label: 'Accueil'}, {key: 'volume_button', label: 'Volume'},
                {key: 'power_button', label: 'Power'}, {key: 'signal_carrier', label: 'Signal'},
                {key: 'battery', label: 'Batterie'}, {key: 'wifi_bt', label: 'Wi-Fi/Bluetooth'},
                {key: 'ear_speaker', label: 'Ecouteur'}, {key: 'charging_port', label: 'Port charge'},
              ].map(p => (
                <button key={p.key} type="button"
                  onClick={() => setEditForm(f => ({...f, parts_check: {...(f.parts_check||{}), [p.key]: !(f.parts_check||{})[p.key]}}))}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    (editForm.parts_check || {})[p.key]
                      ? 'bg-red-500/20 text-red-400 border-red-500/50'
                      : 'bg-white/5 text-gray-400 border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`edit-part-${p.key}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Notes</label>
            <textarea value={editForm.notes} onChange={e => setEditForm(f => ({...f, notes: e.target.value}))} className="input-dark w-full py-2 min-h-[80px]" data-testid="edit-notes" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Prix reparation (EUR)</label>
              <input type="number" step="0.01" min="0" value={editForm.price_repair}
                onChange={e => setEditForm(f => ({...f, price_repair: parseFloat(e.target.value) || 0}))}
                className="input-dark w-full py-2" data-testid="edit-price-repair" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Prix diagnostic (EUR)</label>
              <input type="number" step="0.01" min="0" value={editForm.price_diagnostic}
                onChange={e => setEditForm(f => ({...f, price_diagnostic: parseFloat(e.target.value) || 0}))}
                className="input-dark w-full py-2" data-testid="edit-price-diagnostic" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Mode livraison</label>
              <select value={editForm.delivery_method} onChange={e => setEditForm(f => ({...f, delivery_method: e.target.value}))}
                className="input-dark w-full py-2" data-testid="edit-delivery">
                <option value="boutique">Boutique</option>
                <option value="postal">Postal</option>
                <option value="recuperation">Recuperation</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">N° suivi</label>
              <input value={editForm.tracking_number} onChange={e => setEditForm(f => ({...f, tracking_number: e.target.value}))} className="input-dark w-full py-2" data-testid="edit-tracking" />
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={editForm.back_glass_broken}
                onChange={e => setEditForm(f => ({...f, back_glass_broken: e.target.checked}))}
                className="rounded border-gray-600" data-testid="edit-back-glass" />
              <span className="text-sm text-gray-300">Vitre arriere cassee</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={editForm.customer_provides_part}
                onChange={e => setEditForm(f => ({...f, customer_provides_part: e.target.checked}))}
                className="rounded border-gray-600" data-testid="edit-customer-part" />
              <span className="text-sm text-gray-300">Client fournit la piece</span>
            </label>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={() => setEditRepair(null)}
            className="flex-1 py-3 rounded-xl bg-gray-600/50 hover:bg-gray-600/70 text-gray-200 font-bold text-sm transition-all">
            Annuler
          </button>
          <button onClick={saveEdit} disabled={savingEdit}
            className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-sm transition-all disabled:opacity-50"
            data-testid="edit-save-btn">
            {savingEdit ? 'Sauvegarde...' : 'Modifier'}
          </button>
        </div>
      </div>
    </div>
  );
};
